import React, {
  ForwardRefRenderFunction,
  useImperativeHandle,
  useState,
  useRef,
} from 'react';
import {
  Avatar,
  Button,
  Drawer,
  Modal,
  notification,
  Tag,
  Divider,
} from 'antd';
import { useConnection } from '@sezenta/connection';
import { useTranslation } from 'react-i18next';
import { useEvents } from '../../common/EventContext';
import { useBusinessContext } from '../../context/BusinessContext';
import {
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import CTag from '../../components/ui/CTag';

export type SiteViewModalInstance = {
  view: (site: any) => void;
};
type Props = { onEdit?: (site: any) => void };
type FieldProps = {
  label: string;
  value: React.ReactNode;
};

const Field: React.FC<FieldProps> = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-gray-500 text-sm">{label}</span>
    <span className="font-medium">{value || 'N/A'}</span>
  </div>
);

// Two-column field group component
type FieldGroupProps = {
  fields: { label: string; value: React.ReactNode }[];
};

const FieldGroup: React.FC<FieldGroupProps> = ({ fields }) => (
  <div className="grid grid-cols-2 gap-4">
    {fields.map((field, index) => (
      <Field key={index} label={field.label} value={field.value} />
    ))}
  </div>
);

const SiteViewModal: ForwardRefRenderFunction<SiteViewModalInstance, Props> = (
  props,
  ref,
) => {
  const [site, setSite] = useState<any>();
  const { t } = useTranslation();
  const connection = useConnection();
  const events = useEvents();
  const [deleting, setDeleting] = useState<boolean>(false);
  const [api, contextHolder] = notification.useNotification();
  const { states, zones, suburbs } = useBusinessContext();

  useImperativeHandle(
    ref,
    () => ({
      view: (s: any) => {
        setSite(s);
      },
    }),
    [],
  );

  const deleteSite = () => {
    Modal.confirm({
      title: `${t('sites.delete')}`,
      content: `${t('sites.deleteMsg')}`,
      okText: `${t('sites.deleteOk')}`,
      cancelText: `${t('sites.deleteCancel')}`,
      okButtonProps: { danger: true },
      onOk: () => {
        setDeleting(true);
        connection
          .delete(`sites/${site?.businessId}/${site?.id}`)
          .then(() => {
            api.success({
              message: `${t('sites.deleted')}`,
              description: `${t('sites.deletedText')}`,
            });
            events.emit('SITE', { action: 'DELETE', data: site });
            setSite(undefined);
          })
          .catch(() =>
            Modal.error({
              title: `${t('sites.failed')}`,
              content: `${t('sites.failedText')}`,
            }),
          )
          .finally(() => setDeleting(false));
      },
    });
  };

  const handleEdit = () => {
    if (props.onEdit && site) {
      props.onEdit(site);
      setSite(undefined);
    }
  };

  const hasGeoPosition = site?.latitude && site?.longitude;

  return (
    <>
      <Drawer
        width={480}
        destroyOnClose={true}
        title={
          <div className="flex items-center justify-between">
            <span className="text-lg font-medium">Store Details</span>
          </div>
        }
        open={!!site}
        onClose={() => setSite(undefined)}
        footer={null}
      >
        {site && (
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium mb-2">{site.name}</h3>
              <CTag
                prefix="dot"
                color={site.status === 'Active' ? 'green' : 'orange'}
              >
                {site.status === 'Active' ? 'ACTIVE' : 'INACTIVE'}
              </CTag>
            </div>

            <div className="space-y-4">
              <Field label="Address" value={site.address} />

              <Field label="Store Contact" value={site.storeEmail} />

              <Field label="Status" value={site.status} />

              <Field label="Area" value={site.area} />

              <Field label="Postal Code" value={site.pCode} />

              <Field label="Phone" value={site.phone} />

              <Field label="Email" value={site.email} />

              <FieldGroup
                fields={[
                  { label: 'State', value: states[site.state]?.name },
                  { label: 'Zone', value: zones[site.zone]?.name },
                ]}
              />

              <FieldGroup
                fields={[
                  { label: 'Suburb', value: suburbs[site.suburb]?.name },
                  { label: 'Council', value: site.council },
                ]}
              />

              <FieldGroup
                fields={[
                  { label: 'Store Opening Date', value: site.storeOpeningDate },
                  { label: 'Store Closing Date', value: site.storeClosingDate },
                ]}
              />

              <Field label="Reopening Date" value={site.reopeningDate} />

              {hasGeoPosition && (
                <div className="bg-blue-50 p-3 rounded-md mt-2">
                  <div className="flex items-center gap-2 mb-2">
                    <EnvironmentOutlined className="text-blue-500" />
                    <span className="font-medium">Geo Position</span>
                  </div>
                  <FieldGroup
                    fields={[
                      { label: 'Latitude', value: site.latitude },
                      { label: 'Longitude', value: site.longitude },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </Drawer>
      {contextHolder}
    </>
  );
};

export const useSiteViewModal = () => useRef<SiteViewModalInstance>(null);

export default React.forwardRef<SiteViewModalInstance, Props>(SiteViewModal);
